import Card from '@/components/Card/Card';
import { IsoDateField } from '@/components/Form/Fields/DateField/IsoDateField';
import { InlineAlert, InlineAlertTitle } from '@/components/alert';
import EcoSwitch from '@/components/ecoSwitch/EcoSwitch';
import PostcodeInputCity from '@/components/postcodeInput/PostcodeInputCity';
import { RichText } from '@/components/richText/RichText';
import { TextField } from '@/components/textField';
import { resolveStory } from '@/helper/utils';
import { usePostcodeSelect } from '@/hooks/usePostcodeSelect';
import { useRegionService } from '@/hooks/useRegionService';
import { useCalculatorStore } from '@/providers/CalculatorStoreProvider';
import { useSettings } from '@/providers/SettingsProvider';
import type { PageB2BStoryblok } from '@/types/component-types-sb';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './B2BConsumptionForm.module.scss';
import type { B2BFormData } from './PageB2B';

export interface ConsumptionFormProps {
  pageB2BData: PageB2BStoryblok;
}

const B2BConsumptionForm = forwardRef<HTMLDivElement, ConsumptionFormProps>((props, ref) => {
  const { consumption, consumptionNt, heatflow } = useCalculatorStore((state) => ({
    consumption: state.consumption,
    consumptionNt: state.consumptionNt,
    heatflow: state.heatflow,
  }));
  const { inRegion } = useRegionService();
  const postcodeState = usePostcodeSelect();
  const settings = useSettings();
  const { t } = useTranslation();
  const formik = useFormikContext<B2BFormData>();

  const [hasCity, setHasCity] = useState(false);

  const { pageB2BData } = props;

  const {
    form_consumption_title,
    infomessage_consumption_high_title: consumptionTooHighTitle,
    infomessage_consumption_high: consumptionTooHigh,
    infomessage_consumption_low_title: consumptionTooLowTitle,
    infomessage_consumption_low: consumptionTooLow,
    eco_switch_text: ecoSwitchText,
    eco_switch_info_text: ecoSwitchInfoText,
    regions,
  } = pageB2BData;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ecoTariff = params.get('eco_tariff');
    if (ecoTariff === 'true') {
      formik.setFieldValue('greenEnergy', true);
    }
  }, [formik.setFieldValue]);

  async function isRegion(postCode: string): Promise<boolean> {
    let isInRegion = false;
    if (!regions) {
      return true;
    }

    for (const item of regions) {
      const region = resolveStory(item);
      if (region && (await inRegion(postCode, region.name.toLowerCase()))) {
        isInRegion = true;
      }
    }
    return isInRegion;
  }

  function onSwitchClick() {
    formik.setFieldValue('greenEnergy', !formik.values.greenEnergy);
  }

  const getHeatFlowConsumption = () => {
    const calcConsumption = consumption ?? 0;
    const calcConsumptionNt = consumptionNt ?? 0;
    const heatFlowConsumptionMax = Math.max(calcConsumption, calcConsumptionNt);
    if (heatFlowConsumptionMax < getDefaultConsumption()) {
      return calcConsumption + calcConsumptionNt;
    }
    return heatFlowConsumptionMax;
  };

  const getDefaultConsumption = () => {
    return Number(settings?.calculator_max_consumption_individual) ?? 0;
  };

  const getConsumption = () => {
    const calcConsumption = consumption ?? 0;
    return Math.max(calcConsumption, getDefaultConsumption());
  };

  useEffect(() => {
    const isHeatFlow = heatflow ?? false;
    const currentConsumption = isHeatFlow ? getHeatFlowConsumption() : getConsumption();
    formik.setFieldValue('annualConsumption', currentConsumption);
  }, [heatflow]);

  useEffect(() => {
    formik.setFieldValue('shippingCityName', postcodeState.selectedValue.cityName);
    setHasCity(Boolean(postcodeState.selectedValue.cityName));
  }, [postcodeState.selectedValue.cityName]);

  return (
    <div className={classes.B2BConsumptionForm}>
      <Card className={classes.Card} variant="Box">
        {!!form_consumption_title && <h2 className={classes.Headline}>{form_consumption_title}</h2>}
        <fieldset className="formFieldset">
          <div ref={ref} className="formGrid scroll-margin-header">
            <TextField
              required
              className="halfWidth"
              name="shippingPostcode"
              label={'Postleitzahl'}
              onChange={async (e: React.FocusEvent<any, Element>) => {
                const value = e.target.value;
                postcodeState.handleInputChange(e);
                formik.handleChange(e);
                const isValidRegion = await isRegion(value);
                if (!isValidRegion) {
                  formik.setFieldError('shippingPostcode', t('b2bCalculator.notInRegion'));
                }
              }}
              onBlur={formik.handleBlur}
              value={formik.values.shippingPostcode}
              error={
                formik.touched.shippingPostcode &&
                (Boolean(formik.errors.shippingPostcode) || (!hasCity && Boolean(formik.errors.shippingCityName)))
              }
              helperText={
                formik.touched.shippingPostcode &&
                (formik.errors.shippingPostcode || (!hasCity && formik.errors.shippingCityName))
              }
              inputProps={{ maxLength: 5, inputMode: 'numeric', 'data-testid': 'B2B-consumption-form-zip-input' }}
              InputProps={{
                endAdornment: <PostcodeInputCity state={postcodeState} />,
                inputMode: 'numeric',
              }}
            />
            <TextField
              required
              className="halfWidth"
              id="annualConsumption"
              name="annualConsumption"
              label="Jahresverbrauch"
              type="number"
              value={formik.values.annualConsumption}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.annualConsumption && Boolean(formik.errors.annualConsumption)}
              helperText={formik.touched.annualConsumption && formik.errors.annualConsumption}
              inputProps={{
                'aria-label': 'Kilowattstunde',
                'data-testid': 'B2B-consumption-form-annualConsumption-input',
              }}
              InputProps={{
                endAdornment: <span aria-hidden={true}>kWh</span>,
              }}
            />
            <IsoDateField
              required
              className="halfWidth"
              id="startDate"
              name="startDate"
              label="Gewünschter Lieferbeginn"
              value={formik.values.startDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.startDate && Boolean(formik.errors.startDate)}
              helperText={formik.touched.startDate && formik.errors.startDate}
              slotProps={{
                htmlInput: {
                  min: dayjs().add(3, 'week').format('YYYY-MM-DD'),
                  max: dayjs().add(1, 'year').format('YYYY-MM-DD'),
                  'data-testid': 'B2B-consumption-form-startDate-input',
                },
              }}
            />
            <div className="halfWidth">
              <EcoSwitch
                heading="Ökostrom"
                subtext={ecoSwitchText}
                ecoInfo={ecoSwitchInfoText}
                checked={formik.values.greenEnergy}
                onSwitchClick={onSwitchClick}
              />
            </div>
          </div>
        </fieldset>
      </Card>

      {formik.touched.annualConsumption &&
        Number(formik.values.annualConsumption) > Number(settings?.calculator_max_consumption_b2b) && (
          <InlineAlert severity="info" variant="filled">
            <InlineAlertTitle>{consumptionTooHighTitle}</InlineAlertTitle>
            <RichText text={consumptionTooHigh} />
          </InlineAlert>
        )}
      {formik.touched.annualConsumption &&
        Number(formik.values.annualConsumption) < Number(settings?.calculator_max_consumption_individual) && (
          <InlineAlert severity="info" variant="filled">
            <InlineAlertTitle>{consumptionTooLowTitle}</InlineAlertTitle>
            <RichText text={consumptionTooLow} />
          </InlineAlert>
        )}
    </div>
  );
});

B2BConsumptionForm.displayName = 'B2BConsumptionForm';

export default B2BConsumptionForm;
